import { Input, Text } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { type ChangeEvent, useMemo, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";

import useSavedCursorPosition from "../../hooks/useSavedCursorPosition";
import { useDispatch } from "../../redux/hooks";
import { setUnitConverterModalData } from "../../redux/slices/generatorFeaturesSlice";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import { unitToName } from "./unitToName";
import useNMEAAutomated from "../hooks/useNMEAAutomated";

export interface ISelectOptionsData {
  value: string;
  label: string;
}

interface Props {
  converterName: string;
  value: any;
  selectOptionsData: ISelectOptionsData[];
  id: string;
  sectionIndex?: number;
  name?: string;
  source?: "auto" | "manual";
  rowIndex?: number;
  numberOfCols?: number;
  totalRows?: number;
}

const UnitConverter = ({
  converterName,
  selectOptionsData,
  value,
  id,
  sectionIndex = -1,
  source,
  name,
  rowIndex,
  numberOfCols,
  totalRows,
}: Props) => {
  const dispatch = useDispatch();
  const { ref: addRef, onKeyDown } = useLogbookRefsContext(
    id,
    numberOfCols,
    rowIndex,
    totalRows,
  );
  const { control } = useFormContext();

  const parsedValue = useMemo(() => {
    if (typeof value === "object") {
      return {
        value: value?.value ?? "",
        unit: value?.unit ?? selectOptionsData[0].value,
      };
    }
    return {
      value,
      unit: selectOptionsData[0].value,
    };
  }, [value]);
  const { handleNMEAChange } = useNMEAAutomated({
    id,
    source,
    sectionIndex,
    defaultValue: parsedValue,
    name,
  });
  const {
    field: { onChange, ...field },
  } = useController({
    name: id,
    control,
    defaultValue: parsedValue,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { handleFocus, handleBlur, setCursorPosition } = useSavedCursorPosition(
    id,
    inputRef,
  );
  const handleChangeWithRecording = (value: any) => {
    onChange(value);
    handleNMEAChange(value);
  };
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCursorPosition();
    const inputValue = event.target.value;
    if (inputValue === "" || /^\d+(\.\d*)?$/.test(inputValue)) {
      const newValue = {
        value: inputValue ?? "",
        unit: field.value?.unit ?? selectOptionsData[0].value,
      };
      onChange(newValue);
    }
  };

  const handleInputBlur = (e: any) => {
    handleBlur(e);
    handleNMEAChange(field.value);
  };

  const handleOpen = () => {
    dispatch(
      setUnitConverterModalData({
        converterName,
        selectOptionsData,
        sectionIndex,
        onChange: handleChangeWithRecording,
        unit: field.value.unit,
        value: field.value.value,
      }),
    );
  };
  return (
    <Input
      miw={100}
      value={field.value?.value ?? ""}
      onChange={handleInputChange}
      rightSectionWidth="max-content"
      rightSection={
        <>
          <Text mr="sm" color="text">
            {unitToName[field.value?.unit ?? selectOptionsData[0].value]}
          </Text>
          <IconRefresh
            onClick={handleOpen}
            size="1rem"
            style={{
              marginRight: "5px",
              display: "block",
              opacity: 0.5,
              cursor: "pointer",
            }}
            aria-label="RefreshIcon"
          />
        </>
      }
      ref={r => {
        inputRef.current = r;
        addRef(r);
      }}
      disabled={field.disabled}
      onKeyDown={onKeyDown}
      onFocus={handleFocus}
      onBlur={handleInputBlur}
    />
  );
};

export default UnitConverter;
